import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { BehaviorSubject, catchError, filter, finalize, Observable, of, switchMap, take, throwError, } from 'rxjs';
import { TokenPair } from '../models/responses/token-pair.interface';
import { Router } from '@angular/router';
import Utils from '../../../shared/utils';
import { MessageService } from 'primeng/api';
import { ToastPositionKeys } from '../../models/messages-keys.enum';
import { LocalStorageService } from '../../services/storage/local-storage.service';
import { JwtHelperService } from '../services/jwt-helper.service';
import { AuthService } from '../services/auth.service';
import { log } from "../../../shared/log";

const publicEndpoints: string[] = [
  'v1/auth/login/',
  'v1/auth/register/',
  'v1/auth/token/',
  'v1/auth/token/verify/',
  'v1/auth/token/refresh/',
  'v1/auth/token/blacklist/',
  'v1/web/ping/',
];

let isRefreshing = false;
const refreshTokenSubject: BehaviorSubject<TokenPair | null> = new BehaviorSubject<TokenPair | null>(null);

function addAccessToken(req: HttpRequest<any>, accessToken: string): HttpRequest<any> {
  return req.clone({
    setHeaders: {Authorization: `Bearer ${accessToken}`},
  });
}

function isTokenError(httpError: HttpErrorResponse): boolean {
  if (httpError.status === 403) {
    if (httpError.error?.detail === 'Authentication credentials were not provided.') {
      return true;
    }
    if (httpError.error?.code === 'token_not_valid') {
      return true;
    }
  }
  if (httpError.status === 401) {
    log.error(
      'unspecified 401 error, the error details should be added to the interceptor:',
      httpError
    );
    return true;
  }
  return false;
}

function handleTokenError(
  req: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> {
  const localStorageService = inject(LocalStorageService); // dont delete
  const jwtHelperService = inject(JwtHelperService); // dont delete
  const authService = inject(AuthService);
  const router = inject(Router);

  if (!isRefreshing) {
    log.debug('Initiating token refresh...');
    isRefreshing = true;
    refreshTokenSubject.next(null);

    return authService.refreshTokens().pipe(
      switchMap((tokenPair: TokenPair) => {
        isRefreshing = false;
        log.debug('Token Refresh was successful!');
        refreshTokenSubject.next(tokenPair);
        return next(addAccessToken(req, tokenPair.access));
      }),
      catchError((error) => {
        isRefreshing = false;
        log.debug('Token Refresh failed:', error);
        if (error === 'refresh_token_invalid') {
          authService.clearLocalUserData();
          router.navigate(Utils.getLoginRouteWithRedirect(router));
        }
        return throwError(() => error);
      }),
      finalize(() => {
        isRefreshing = false;
      })
    );
  } else {
    log.debug('A token refresh is already in progress, waiting for it to complete...');
    return refreshTokenSubject.pipe(
      filter((token) => token != null),
      take(1),
      switchMap((tokenPair: TokenPair) => {
        log.debug('Token refresh finished, pending request gets sent with new access token now.');
        return next(addAccessToken(req, tokenPair.access));
      })
    );
  }
}

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> => {
  const localStorageService = inject(LocalStorageService);
  const jwtHelperService = inject(JwtHelperService);
  const authService = inject(AuthService);
  const router = inject(Router);
  const messageService = inject(MessageService);

  const isPublicEndpoint = publicEndpoints.some((endpoint) =>
    req.url.includes(environment.oskaruApiUrl + endpoint)
  );
  if (isPublicEndpoint) {
    return next(req);
  }

  const accessToken: string | null = localStorageService.getAccessToken();

  if (!!accessToken) {
    if (jwtHelperService.isTokenExpired(accessToken)) {
      return handleTokenError(req, next);
    }

    const modifiedReq = addAccessToken(req, accessToken);
    return next(modifiedReq).pipe(
      catchError((httpError: HttpErrorResponse) => {
        if (isTokenError(httpError)) {
          return handleTokenError(req, next);
        } else {
          return throwError(() => httpError);
        }
      })
    );
  }

  return next(req).pipe(
    catchError((httpError: HttpErrorResponse) => {
      if (isTokenError(httpError)) {
        const loginRouteWithRedirect = Utils.getLoginRouteWithRedirect(router);
        authService.clearLocalUserData();
        router.navigate(loginRouteWithRedirect);
        messageService.add({
          key: ToastPositionKeys.TOP_CENTER,
          severity: 'error',
          summary: 'Login required!',
          detail: 'You need to be logged in to complete this action.',
        });
        return of();
      }
      return throwError(() => httpError);
    })
  );
};
